import {
  doc,
  updateDoc,
  increment,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import { db } from './firebase';

export async function getUserCredits(userId) {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    const data = userDoc.data();
    const regularCredits = data.credits || 0;
    const premiumCredits = data.premiumCredits || 0;

    return {
      regular: regularCredits,
      premium: premiumCredits,
      total: regularCredits + premiumCredits
    };
  } catch (error) {
    console.error('Error getting user credits:', error);
    throw new Error('Failed to get credit balance');
  }
}

export async function updateUserCredits(userId, amount) {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      premiumCredits: increment(amount) // Update premium credits instead of regular credits
    });
  } catch (error) {
    console.error('Error updating user credits:', error);
    throw new Error('Failed to update credit balance');
  }
}

// Deduct credits for disease detection (costs 5 credits)
export async function deductDiseaseDetectionCredits(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const credits = await getUserCredits(userId);

    // Check if user has enough total credits
    if (credits.total < 5) {
      throw new Error(
        'Insufficient credits for disease detection (requires 5 credits)'
      );
    }

    // Calculate how many credits to take from each type
    let regularToDeduct = Math.min(credits.regular, 5);
    let premiumToDeduct = 0;

    // If regular credits aren't enough, use premium credits for the remainder
    if (regularToDeduct < 5) {
      premiumToDeduct = 5 - regularToDeduct;
    }

    // Update both credit types in a single operation
    await updateDoc(userRef, {
      credits: increment(-regularToDeduct),
      premiumCredits: increment(-premiumToDeduct)
    });

    return {
      regular: credits.regular - regularToDeduct,
      premium: credits.premium - premiumToDeduct,
      total: credits.total - 5
    };
  } catch (error) {
    console.error('Error deducting disease detection credits:', error);
    throw error;
  }
}

export async function deductIdentificationCredit(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const credits = await getUserCredits(userId);

    // Check if user has enough total credits
    if (credits.total < 1) {
      throw new Error('Insufficient credits for identification');
    }

    // Determine which type of credit to deduct
    if (credits.regular > 0) {
      // Deduct from regular credits first
      await updateDoc(userRef, {
        credits: increment(-1)
      });
      return {
        regular: credits.regular - 1,
        premium: credits.premium,
        total: credits.total - 1
      };
    } else {
      // If no regular credits, deduct from premium
      await updateDoc(userRef, {
        premiumCredits: increment(-1)
      });
      return {
        regular: 0,
        premium: credits.premium - 1,
        total: credits.total - 1
      };
    }
  } catch (error) {
    console.error('Error deducting identification credit:', error);
    throw error;
  }
}

export async function purchaseCredits(userId, amount) {
  try {
    // Purchase credits are saved as premium credits that don't expire
    await updateUserCredits(userId, amount, true);

    // Save purchase history
    const purchaseHistoryRef = collection(
      db,
      'users',
      userId,
      'creditPurchases'
    );
    await addDoc(purchaseHistoryRef, {
      amount: amount,
      cost: amount * 10, // ₹10 per credit
      timestamp: serverTimestamp(),
      status: 'completed',
      type: 'premium' // Mark these credits as premium
      // No expiration date for premium credits
    });

    return true;
  } catch (error) {
    console.error('Error purchasing credits:', error);
    throw new Error('Failed to process payment');
  }
}

// Get purchase history
export async function getPurchaseHistory(userId) {
  try {
    const purchaseHistoryRef = collection(
      db,
      'users',
      userId,
      'creditPurchases'
    );
    const q = query(purchaseHistoryRef, orderBy('timestamp', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate(),
      // Only include expiresAt if it exists (for old records)
      ...(doc.data().expiresAt && { expiresAt: doc.data().expiresAt.toDate() })
    }));
  } catch (error) {
    console.error('Error getting purchase history:', error);
    throw new Error('Failed to fetch purchase history');
  }
}
